import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiExternalLink } from 'react-icons/fi';
import {
  BUY_LABEL,
  COMING_SOON_LABEL,
  getHeroBuyableBooks,
  LIBRARY_BY_ID,
  STATUS,
} from '../data/library';
import HeroStarExplosion from './HeroStarExplosion';
import heroFiller1 from '../assets/hero-filler-1.png';
import heroFiller2 from '../assets/hero-filler-2.png';
import heroFiller3 from '../assets/hero-filler-3.png';
import heroFiller4 from '../assets/hero-filler-4.png';
import heroFiller5 from '../assets/hero-filler-5.png';
import heroFbPlushComingSoon from '../assets/hero-fb-plush-coming-soon.png';
import libbyPlushyPromoVideo from '../assets/libby-plushy-promo.mp4';

/** Split a product title into two hero headline lines (matches Amazon section titles). */
function heroTitleLines(title) {
  const colonIdx = title.indexOf(': ');
  if (colonIdx >= 0) {
    return { line1: title.slice(0, colonIdx), line2: title.slice(colonIdx + 2) };
  }
  const abcIdx = title.indexOf(' ABC ');
  if (abcIdx >= 0) {
    return { line1: title.slice(0, abcIdx), line2: title.slice(abcIdx + 1) };
  }
  return { line1: title, line2: '' };
}

function slideFromProduct({ productId, ...rest }) {
  const book = LIBRARY_BY_ID[productId];
  if (!book) return rest;

  const { line1, line2 } = heroTitleLines(book.title);
  const base = {
    ...rest,
    line1,
    line2,
    tagline: book.tagline,
  };

  if (book.status === STATUS.BUY_NOW) {
    return {
      ...base,
      eyebrow: book.amazonEyebrow || BUY_LABEL,
      buyUrl: book.buyUrl,
      buyLabel: BUY_LABEL,
    };
  }

  return {
    ...base,
    eyebrow: COMING_SOON_LABEL,
    buyUrl: undefined,
    buyLabel: undefined,
  };
}

const BRAND_SLIDE_DEFS = [
  {
    id: 'kites-meadow',
    image: heroFiller1,
    eyebrow: "Welcome to Libby's world",
    line1: 'Libby the Lion',
    line2: '& Friends',
    tagline:
      'Stories, coloring adventures, and characters kids can cheer for — built on kindness, curiosity, and confidence.',
  },
  {
    id: 'bunting-path',
    image: heroFiller2,
    eyebrow: 'Adventures await',
    line1: 'Friends who',
    line2: 'stick together',
    tagline:
      'Join Libby and friends on colorful journeys filled with laughter, discovery, and heart.',
  },
  {
    id: 'abc-school',
    image: heroFiller3,
    eyebrow: 'Learn & play',
    line1: 'Back to school',
    line2: 'with Libby',
    tagline:
      'ABC adventures, coloring fun, and characters kids love — perfect for curious young learners.',
  },
  {
    id: 'kites-soar',
    image: heroFiller4,
    eyebrow: 'Soar with us',
    line1: 'Up, up,',
    line2: 'and away',
    tagline:
      "Let imagination take flight with stories and adventures from Libby's magical world.",
  },
  {
    id: 'scavenger-hunt',
    image: heroFiller5,
    eyebrow: 'Thanks for visiting',
    line1: 'Every day is',
    line2: 'an adventure',
    tagline:
      'You have found the home of Libby the Lion & Friends — a magical world that keeps expanding with new friends, stories, and adventures.',
  },
];

const BUY_SLIDE_DEFS = getHeroBuyableBooks().map(book => ({
  id: `buy-${book.id}`,
  image: book.cover,
  productId: book.id,
}));

/** Brand intro, then Facebook feature, then buy-now titles, then more brand slides. */
const FB_PLUSH_SLIDE = {
  id: 'fb-plush-coming-soon',
  image: heroFbPlushComingSoon,
  eyebrow: 'COMING SOON!',
  line1: 'Libby the Lion',
  line2: 'Plushy',
  tagline:
    'Libby has been busy creating new and fun things for your little ones — your favorite lion cub is ready to roar her way into your heart, with plush toys, books, stickers, backpacks, and more on the way.',
};

const FB_PLUSH_VIDEO_SLIDE = {
  id: 'fb-plush-video',
  image: heroFbPlushComingSoon,
  videoSrc: libbyPlushyPromoVideo,
  eyebrow: 'COMING SOON!',
  line1: 'Libby the Lion',
  line2: 'Plushy',
  tagline:
    'Watch the reveal — Libby the Lion plush is on the way, with books, stickers, backpacks, and more fun for your little ones.',
};

const SLIDE_DEFS = [
  ...BRAND_SLIDE_DEFS.slice(0, 2),
  FB_PLUSH_SLIDE,
  FB_PLUSH_VIDEO_SLIDE,
  ...BUY_SLIDE_DEFS,
  ...BRAND_SLIDE_DEFS.slice(2),
];

const SLIDES = SLIDE_DEFS.map(def =>
  def.productId ? slideFromProduct(def) : { ...def, buyUrl: undefined, buyLabel: undefined }
);

export const HERO_INTERVAL_MS = 12000;
const SLIDE_FADE_MS = 3000;
const TEXT_EXIT_MS = 550;
const TEXT_ENTER_DELAY_MS = 650;

function HeroSlideMedia({ slide, videoActive = false, playbackPaused = false }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !slide.videoSrc) return undefined;

    if (videoActive && !playbackPaused && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const playAttempt = el.play();
      if (playAttempt) playAttempt.catch(() => {});
    } else {
      el.pause();
      if (!videoActive) el.currentTime = 0;
    }

    return undefined;
  }, [slide.videoSrc, videoActive, playbackPaused]);

  if (slide.videoSrc) {
    return (
      <video
        ref={videoRef}
        className="hero-slider__layer-video"
        src={slide.videoSrc}
        autoPlay={videoActive}
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden
        tabIndex={-1}
      />
    );
  }

  return (
    <img
      src={slide.image}
      alt=""
      className="hero-slider__layer-img"
      decoding="async"
      draggable={false}
    />
  );
}

export default function HeroSlider() {
  const [index, setIndex] = useState(0);
  const [textIndex, setTextIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);
  const [copyShown, setCopyShown] = useState(false);
  const [hoverPaused, setHoverPaused] = useState(false);
  const [transitionKey, setTransitionKey] = useState(0);
  const leaveTimer = useRef(null);
  const textTimer = useRef(null);
  const indexRef = useRef(0);
  const count = SLIDES.length;

  indexRef.current = index;

  useEffect(() => {
    const preload = document.createElement('video');
    preload.preload = 'auto';
    preload.muted = true;
    preload.playsInline = true;
    preload.src = libbyPlushyPromoVideo;
    preload.load();
    return () => {
      preload.src = '';
    };
  }, []);

  const selectSlide = useCallback(
    next => {
      const normalized = ((next % count) + count) % count;
      if (normalized === indexRef.current) return;

      if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
      if (textTimer.current) window.clearTimeout(textTimer.current);

      setCopyShown(false);
      setPrevIndex(indexRef.current);
      setIndex(normalized);
      setTransitionKey(k => k + 1);

      textTimer.current = window.setTimeout(() => {
        setTextIndex(normalized);
      }, TEXT_EXIT_MS);

      leaveTimer.current = window.setTimeout(() => setPrevIndex(null), SLIDE_FADE_MS);
    },
    [count]
  );

  const go = useCallback(dir => selectSlide(indexRef.current + dir), [selectSlide]);

  useEffect(() => {
    return () => {
      if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
      if (textTimer.current) window.clearTimeout(textTimer.current);
    };
  }, []);

  useEffect(() => {
    if (hoverPaused) return undefined;
    const id = window.setInterval(() => go(1), HERO_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [hoverPaused, go]);

  useLayoutEffect(() => {
    setCopyShown(false);
    const enterTimer = window.setTimeout(() => {
      window.requestAnimationFrame(() => setCopyShown(true));
    }, TEXT_ENTER_DELAY_MS);
    return () => window.clearTimeout(enterTimer);
  }, [textIndex]);

  const slide = SLIDES[textIndex];
  const isTransitioning = prevIndex !== null;
  const videoSlideActive = !isTransitioning && Boolean(SLIDES[index]?.videoSrc);

  return (
    <section
      className={`hero-slider${isTransitioning ? ' is-transitioning' : ''}${hoverPaused ? ' hero-slider--paused' : ''}`}
      style={{
        '--hero-interval': `${HERO_INTERVAL_MS}ms`,
        '--hero-fade-ms': `${SLIDE_FADE_MS}ms`,
        '--hero-text-duration': '600ms',
        '--hero-text-enter-delay': `${TEXT_ENTER_DELAY_MS}ms`,
      }}
      aria-roledescription="carousel"
      aria-label="Featured"
    >
      <div className="hero-slider__media" aria-hidden>
        <div className="hero-slider__layers">
          {isTransitioning ? (
            <>
              <div
                key={`out-${prevIndex}-${transitionKey}`}
                className="hero-slider__layer hero-slider__layer--out"
              >
                <HeroSlideMedia
                  slide={SLIDES[prevIndex]}
                  videoActive={Boolean(SLIDES[prevIndex]?.videoSrc)}
                  playbackPaused={hoverPaused}
                />
              </div>
              <div key={`in-${index}-${transitionKey}`} className="hero-slider__layer hero-slider__layer--in">
                <HeroSlideMedia
                  slide={SLIDES[index]}
                  videoActive={Boolean(SLIDES[index]?.videoSrc)}
                  playbackPaused={hoverPaused}
                />
              </div>
            </>
          ) : (
            <div className="hero-slider__layer hero-slider__layer--hold">
              <HeroSlideMedia slide={SLIDES[index]} videoActive={videoSlideActive} playbackPaused={hoverPaused} />
            </div>
          )}
        </div>

        <div className="hero-slider__overlay" />

        {isTransitioning ? <HeroStarExplosion key={`explosion-${transitionKey}`} /> : null}
      </div>

      <div className="hero-slider__content">
        <div className="hero-slider__stack">
          <div
            className={`hero-slider__copy${copyShown ? ' is-shown' : ''}`}
            key={textIndex}
            aria-live="polite"
          >
            <div className="hero-slider__copy-body">
              <p className="hero-slider__eyebrow">
                <span className="hero-slider__anim hero-slider__anim--from-top">{slide.eyebrow}</span>
              </p>
              <h1 className="hero-slider__title">
                <span className="hero-slider__title-line">
                  <span className="hero-slider__anim hero-slider__anim--from-bottom">{slide.line1}</span>
                </span>
                <span className="hero-slider__title-line hero-slider__title-line--accent">
                  <span className="hero-slider__anim hero-slider__anim--from-bottom hero-slider__anim--delay-2">
                    {slide.line2}
                  </span>
                </span>
              </h1>
              <p className="hero-slider__tagline">
                <span className="hero-slider__anim hero-slider__anim--from-top hero-slider__anim--delay-3">
                  {slide.tagline}
                </span>
              </p>
            </div>
          </div>

          {slide.buyUrl ? (
            <a
              key={`buy-${textIndex}`}
              href={slide.buyUrl}
              className="btn-buy-amazon hero-slider__amazon-cta"
              target="_blank"
              rel="noopener noreferrer"
            >
              {slide.buyLabel || BUY_LABEL}
              <FiExternalLink size={20} aria-hidden />
              <span className="visually-hidden"> (opens in new tab)</span>
            </a>
          ) : null}

          <div
            className="hero-slider__controls"
            aria-label="Slide controls"
            onMouseEnter={() => setHoverPaused(true)}
            onMouseLeave={() => setHoverPaused(false)}
          >
            <button type="button" className="hero-slider__arrow" onClick={() => go(-1)} aria-label="Previous slide">
              <FiChevronLeft size={24} aria-hidden />
              <span className="hero-slider__arrow-label">Prev</span>
            </button>
            <div className="hero-slider__dots" role="tablist" aria-label="Choose slide">
              {SLIDES.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`Slide ${i + 1}: ${s.line1}`}
                  className={`hero-slider__dot${i === index ? ' is-active' : ''}`}
                  onClick={() => selectSlide(i)}
                />
              ))}
            </div>
            <button type="button" className="hero-slider__arrow" onClick={() => go(1)} aria-label="Next slide">
              <span className="hero-slider__arrow-label">Next</span>
              <FiChevronRight size={24} aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
